"use client";

import { useState, useEffect, useRef, useCallback, type CSSProperties } from "react";
import type { WorkSession, WorkUpdateEntry } from "../hooks/useWork";
import type { WorkSummary } from "../store/types";

// ─── Static styles (stable references, no re-alloc per render) ──────────────

const wrapperStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "0.15rem 0",
  minWidth: 0,
  maxWidth: "100%",
};

const headerBtnStyle: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: "0.65rem",
  color: "var(--color-text-tertiary)",
  marginBottom: 2,
  fontFamily: "inherit",
};

const bodyStyle: CSSProperties = {
  maxWidth: "85%",
  maxHeight: "60vh",
  padding: "0.35rem 0.6rem",
  borderRadius: 8,
  background: "transparent",
  border: "1px solid transparent",
  fontSize: "0.75rem",
  color: "var(--color-text-muted)",
  lineHeight: 1.5,
  overflowX: "hidden",
  overflowY: "hidden",
};

const expandedBodyStyle: CSSProperties = {
  ...bodyStyle,
  maxHeight: 400,
  overflowY: "auto",
};

const dotBase: CSSProperties = {
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: "50%",
};

const metaStyle: CSSProperties = { marginLeft: 4, color: "var(--color-text-faint)" };
const contentStyle: CSSProperties = { whiteSpace: "pre-wrap", wordBreak: "break-word" };
const toolLabelStyle: CSSProperties = { color: "#4a7a4a", fontSize: "0.7rem", fontWeight: 500, marginBottom: 2 };
const thinkLabelStyle: CSSProperties = { color: "#6a6a3a", fontSize: "0.7rem", fontWeight: 500, marginBottom: 2 };
const loadingStyle: CSSProperties = { color: "var(--color-text-faint)" };

// ─── Two-line collapsed styles ──────────────────────────────────────────────

const narrationStyle: CSSProperties = {
  fontSize: "0.72rem",
  color: "var(--color-text-secondary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.4,
};

const activityLineStyle: CSSProperties = {
  fontSize: "0.65rem",
  color: "#4a7a4a",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginTop: 3,
  lineHeight: 1.4,
};

const activityThinkingStyle: CSSProperties = {
  ...activityLineStyle,
  color: "#6a6a3a",
};

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
  session: WorkSession;
  agentName: string | null;
  onLoadUpdates: (sessionId: string) => void;
}

export function WorkBubble({ session, agentName, onLoadUpdates }: Props) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const collapsedRef = useRef<HTMLDivElement>(null);
  const label = agentName ?? "Agent";
  const isActive = !session.completedAt && !session.stale;
  const isStale = !!session.stale && !session.completedAt;
  const latestUpdate = session.updates.length > 0
    ? session.updates[session.updates.length - 1]
    : null;

  // Latest "text" narration (top line of collapsed view), default "Working…"
  const latestNarrationText = (() => {
    for (let i = session.updates.length - 1; i >= 0; i--) {
      if (session.updates[i].updateType === "text") return session.updates[i].content;
    }
    return "Working\u2026";
  })();

  // Latest non-text action (bottom line: "tool + context")
  const latestAction = (() => {
    for (let i = session.updates.length - 1; i >= 0; i--) {
      if (session.updates[i].updateType !== "text") return session.updates[i];
    }
    return null;
  })();

  // High-water-mark dimensions for the collapsed active box
  const hwmRef = useRef({ width: 0, height: 0 });
  const [minSize, setMinSize] = useState({ width: 0, height: 0 });

  // Measure the collapsed box after each render and ratchet up
  // Cap minHeight at 60vh so the high-water-mark respects the viewport limit
  const measureCollapsed = useCallback(() => {
    if (!collapsedRef.current || !isActive) return;
    const { scrollWidth, scrollHeight } = collapsedRef.current;
    const maxH = Math.floor(window.innerHeight * 0.6);
    const hwm = hwmRef.current;
    const clampedHeight = Math.min(scrollHeight, maxH);
    if (scrollWidth > hwm.width || clampedHeight > hwm.height) {
      hwm.width = Math.max(hwm.width, scrollWidth);
      hwm.height = Math.max(hwm.height, clampedHeight);
      setMinSize({ width: hwm.width, height: hwm.height });
    }
  }, [isActive]);

  // Measure after every update change
  useEffect(() => {
    measureCollapsed();
  }, [session.updates.length, measureCollapsed]);

  // Auto-scroll within expanded bubble when new updates arrive
  useEffect(() => {
    if (expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [expanded, session.updates.length]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !session.updatesLoaded && session.updates.length === 0) {
      onLoadUpdates(session.sessionId);
    }
  };

  const dotStyle: CSSProperties = {
    ...dotBase,
    background: isActive ? "#8a7a30" : isStale ? "#8a4a30" : "transparent",
    animation: isActive ? "pulse 1.5s ease-in-out infinite" : "none",
  };

  // Show the collapsed box while work is active (not stale, not completed)
  const showCollapsedBox = !expanded && isActive;

  const collapsedStyle: CSSProperties = {
    ...bodyStyle,
    ...(minSize.width > 0 ? { minWidth: minSize.width, minHeight: minSize.height } : {}),
    overflow: "hidden",
  };

  return (
    <div style={wrapperStyle}>
      {/* Clickable header */}
      <button onClick={handleToggle} style={headerBtnStyle}>
        <span style={dotStyle} />
        <span>
          {label}{isStale ? " timed out" : !isActive ? " worked" : ""}
          {!isActive && !isStale && !expanded && (
            <span style={metaStyle}>
              {"\u25B8"} {formatWorkStats(session)}
            </span>
          )}
          {isStale && !expanded && (
            <span style={{ ...metaStyle, color: "#8a4a30" }}>
              {"\u25B8"} no response received
            </span>
          )}
          {expanded && <span style={metaStyle}>{"\u25BE"}</span>}
        </span>
      </button>

      {/* Collapsed active: two-line layout — narration + current activity */}
      {showCollapsedBox && (
        <div ref={collapsedRef} style={collapsedStyle}>
          {/* Top line: narration text (defaults to "Working…") */}
          <div style={narrationStyle}>{latestNarrationText}</div>
          {/* Bottom line: current activity — tool + context */}
          {latestAction && (
            <div style={latestAction.updateType === "thinking" ? activityThinkingStyle : activityLineStyle}>
              {formatActivity(latestAction)}
            </div>
          )}
        </div>
      )}

      {/* Expanded: full update stream */}
      {expanded && (
        <div ref={scrollRef} style={expandedBodyStyle}>
          {!session.updatesLoaded && session.updates.length === 0 && (
            <div style={loadingStyle}>Loading work stream...</div>
          )}
          {session.updates.map((entry, i) => (
            <div
              key={entry.id ?? i}
              style={{ marginBottom: i < session.updates.length - 1 ? 8 : 0 }}
            >
              <UpdateBlock entry={entry} />
            </div>
          ))}
          {isActive && <span style={loadingStyle}>...</span>}
        </div>
      )}
    </div>
  );
}

/** Format token counts compactly: 1234 → "1.2k", 500 → "500" */
function fmtTokens(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(n);
}

/** Truncate a string value to a sane display length */
function truncVal(v: unknown, max = 80): string {
  const s = String(v);
  return s.length > max ? s.slice(0, max - 3) + "..." : s;
}

/** Render structured summary data as key-value display parts */
function renderSummaryParts(summary: WorkSummary): string[] {
  const d = summary.data ?? {};

  if (summary.type === "claudeAgentSdk") {
    const parts: string[] = [];
    const cost = d.total_cost_usd as number | undefined;
    if (typeof cost === "number" && cost > 0) {
      parts.push(`$${cost < 0.01 ? cost.toFixed(3) : cost.toFixed(2)}`);
    }
    const turns = d.num_turns as number | undefined;
    if (typeof turns === "number" && turns > 0) {
      parts.push(`${turns} turn${turns !== 1 ? "s" : ""}`);
    }
    const usage = d.usage as Record<string, number> | undefined;
    if (usage) {
      if (usage.input_tokens > 0) parts.push(`${fmtTokens(usage.input_tokens)} in`);
      if (usage.output_tokens > 0) parts.push(`${fmtTokens(usage.output_tokens)} out`);
      if (usage.cache_read_input_tokens > 0) parts.push(`${fmtTokens(usage.cache_read_input_tokens)} cached`);
    }
    return parts;
  }

  if (summary.type === "openclaws") {
    const parts: string[] = [];
    if (d.error) parts.push(`error: ${truncVal(d.error, 60)}`);
    return parts;
  }

  // Generic fallback: render all non-null values
  return Object.entries(d)
    .filter(([, v]) => v != null)
    .map(([, v]) => truncVal(v));
}

/** Format duration + tool count + summary for completed work header */
function formatWorkStats(session: WorkSession): string {
  const parts: string[] = [];

  // Duration
  if (session.startedAt && session.completedAt) {
    const ms = new Date(session.completedAt).getTime() - new Date(session.startedAt).getTime();
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 60) {
      parts.push(`${totalSec}s`);
    } else {
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      parts.push(sec > 0 ? `${min}m ${sec}s` : `${min}m`);
    }
  }

  // Tool count
  const toolCount = session.updates.filter((u) => u.updateType === "tool_use").length;
  if (toolCount > 0) {
    parts.push(`${toolCount} tool${toolCount !== 1 ? "s" : ""}`);
  }

  // Structured summary object
  if (session.summary && typeof session.summary === "object") {
    parts.push(...renderSummaryParts(session.summary));
  } else if (typeof session.summary === "string") {
    // Legacy string summaries still exist in the DB
    const s = session.summary as unknown as string;
    const maxLen = 60 - parts.join(", ").length;
    if (maxLen > 15) {
      parts.push(s.length > maxLen ? s.slice(0, maxLen - 3) + "..." : s);
    }
  }

  return parts.length > 0 ? parts.join(", ") : "done";
}

/** Extract a short context string from tool_use JSON content */
function extractToolContext(content: string | undefined): string {
  if (!content) return "";
  try {
    const input = JSON.parse(content);
    // File-based tools: show just the filename
    if (input.file_path) {
      const parts = String(input.file_path).split("/");
      return parts[parts.length - 1];
    }
    // Glob/Grep: show the pattern
    if (input.pattern) return String(input.pattern);
    // Bash: show the command (truncated)
    if (input.command) {
      const cmd = String(input.command);
      return cmd.length > 60 ? cmd.slice(0, 57) + "..." : cmd;
    }
    // Agent: show the description
    if (input.description) return String(input.description);
    // WebSearch/WebFetch: show the query or URL
    if (input.query) return String(input.query);
    if (input.url) return String(input.url);
  } catch {
    // not JSON — show raw content snippet
    if (content.length > 60) return content.slice(0, 57) + "...";
    return content;
  }
  return "";
}

function formatActivity(entry: WorkUpdateEntry): string {
  if (entry.updateType === "thinking") return "\uD83D\uDCAD Thinking...";
  const name = entry.toolName ?? "tool";
  const ctx = extractToolContext(entry.content);
  return ctx ? `\u26A1 ${name}: ${ctx}` : `\u26A1 ${name}...`;
}

function UpdateBlock({ entry }: { entry: WorkUpdateEntry }) {
  return (
    <>
      {entry.updateType === "tool_use" && (
        <div style={toolLabelStyle}>
          {"\u26A1"} {entry.toolName ?? "tool"}
        </div>
      )}
      {entry.updateType === "thinking" && (
        <div style={thinkLabelStyle}>
          {"\uD83D\uDCAD"} thinking
        </div>
      )}
      <div style={contentStyle}>{entry.content}</div>
    </>
  );
}
